export type Session = {
  authenticated: boolean;
  csrfToken?: string;
  displayName?: string;
  expiresAt?: string;
};

export type Category = {
  id: string;
  slug: string;
  name: string;
  objectiveKpi: number;
  monthlyQuota: number;
  writer: 'gemini' | 'anthropic';
  description: string;
  templateSlug: string | null;
  visualTemplateFamilySlug: string | null;
  /** Données factuelles que la catégorie exige avant toute rédaction. */
  expectedVariables: Record<string, string> | null;
  networks: string[];
};

export type Settings = {
  categories: Category[];
  templates: Array<{ slug: string; name: string; format: 'square' | 'feed' | 'story' }>;
  visualFamilies: Array<{ slug: string; name: string }>;
  networks: Array<{ code: string; label: string; enabled: boolean }>;
  brandAssets: { portrait: string | null };
};

export type NetworkAccounts = {
  accounts: Array<{
    network: 'instagram' | 'facebook' | 'linkedin' | 'gbp';
    displayName: string;
    status: 'active' | 'expiring' | 'revoked';
    lastError: string | null;
    expiresAt: string | null;
  }>;
  available: { meta: boolean; linkedin: boolean };
};

export type PostSummary = {
  id: string;
  status: 'draft' | 'generated' | 'pending_review' | 'approved' | 'rejected' | 'published' | 'failed';
  scheduledFor: string | null;
  reviewedAt: string | null;
  publishedAt: string | null;
  createdAt: string;
  category: { id: string; slug: string; name: string };
  topic: { id: string; title: string };
  mediaId: string | null;
  mediaKind: 'image' | 'video' | 'thumbnail' | null;
  /** Renseigné quand un gabarit existait mais n'a pas pu être composé : le texte reste valable. */
  visualError: string | null;
};

export type Topic = {
  id: string;
  title: string;
  source: 'manual' | 'watch' | 'review' | 'listing';
  status: 'new' | 'ready' | 'used' | 'discarded';
  createdAt: string;
  category: { id: string; slug: string; name: string };
  /** Ancien champ conservé pendant la migration du modèle média. */
  heroMediaId: string | null;
  /** Montage Microsoft sans cartouche ni informations du bien. */
  sourceVideoId: string | null;
  /** Photo utilisée pour produire la sortie PNG et les réseaux sans vidéo. */
  fallbackImageId: string | null;
  videoTemplateFamily: { slug: string; name: string } | null;
};

export type UploadedMedia = {
  mediaId: string;
  kind: 'image' | 'video';
  role: 'source_video' | 'fallback_image';
  byteSize: number;
};

export type VideoFrame = { x: number; y: number; width: number; height: number };
export type VideoTemplateField = {
  key: string;
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: number;
  minFontSize: number;
  maxLines: number;
  fit: 'shrink' | 'fixed';
  fontFamily: 'Manrope' | 'Playfair Display' | 'Caveat' | 'Montserrat' | 'JetBrains Mono';
  color: string;
  align: 'left' | 'center' | 'right';
  weight?: number;
};
export type VideoTemplate = {
  id: string;
  slug: string;
  name: string;
  version: number;
  format: 'square' | 'feed' | 'story' | 'landscape_video' | 'square_video' | 'portrait_video' | 'reel_video';
  width: number;
  height: number;
  backgroundMediaId: string | null;
  videoFrame: VideoFrame | null;
  fields: VideoTemplateField[] | null;
  videoLayer: 'below' | 'above' | null;
  mediaKind: 'none' | 'image' | 'video';
  /** Le modèle HTML contient sa propre fenêtre vidéo : aucun fond PNG à régler. */
  hasMediaSlot: boolean;
  familySlug: string | null;
  familyName: string | null;
  categoryId: string | null;
  categoryName: string | null;
  slideRole: 'cover' | 'detail';
};

export type GenerationOutcome = {
  postId: string;
  writer: 'gemini' | 'anthropic';
  downgradedBecause: string | null;
  costEur: number;
  /** Média composé depuis le gabarit de la catégorie, ou null sans gabarit rattaché. */
  mediaId: string | null;
  /** Motif de l'absence de visuel. Le post existe quand même : son texte est publiable. */
  visualError: string | null;
};

export type PostVariant = {
  id: string;
  network: string;
  body: string;
  hashtags: string[];
  ctaUrl: string | null;
  writerUsed: string | null;
  costEur: number | null;
  mediaId: string | null;
  mediaKind: 'image' | 'video' | 'thumbnail' | null;
};

export class ApiError extends Error {
  public constructor(
    message: string,
    public readonly code: string,
    public readonly status: number,
  ) {
    super(message);
  }
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  csrfToken?: string | undefined;
};

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  // Toute requête qui modifie l'état porte le jeton anti-CSRF. Le cookie est
  // SameSite=strict, mais on ne fait pas reposer la sécurité sur le seul
  // comportement du navigateur.
  if (method !== 'GET' && options.csrfToken) headers['X-CSRF-Token'] = options.csrfToken;

  const response = await fetch(path, {
    method,
    headers,
    credentials: 'same-origin',
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
  });

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const detail = (payload as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(
      detail?.message ?? 'La requête a échoué.',
      detail?.code ?? 'UNKNOWN',
      response.status,
    );
  }
  return payload as T;
}

export const api = {
  me: () => request<Session>('/api/auth/me'),
  login: (email: string, password: string) =>
    request<Session>('/api/auth/login', { method: 'POST', body: { email, password } }),
  logout: (csrfToken: string | undefined) =>
    request<Session>('/api/auth/logout', { method: 'POST', csrfToken }),
  health: () => request<{ status: string; commit: string }>('/health'),

  categories: () => request<{ categories: Category[] }>('/api/categories'),
  settings: () => request<Settings>('/api/settings'),
  networkAccounts: () => request<NetworkAccounts>('/api/network-accounts'),
  updateCategorySettings: (
    categoryId: string,
    settings: { monthlyQuota: number; writer: 'gemini' | 'anthropic'; templateSlug: string | null; visualTemplateFamilySlug: string | null; networks: string[] },
    csrfToken: string | undefined,
  ) => request<{ updated: true }>(`/api/categories/${categoryId}/settings`, {
    method: 'PUT', body: settings, csrfToken,
  }),
  importVisualTemplate: async (name: string, categoryId: string, file: File, csrfToken: string | undefined): Promise<{ template: { id: string; slug: string; name: string; version: number; categoryId: string; familySlug: string; format: 'square' | 'feed' | 'story' } }> => {
    const headers: Record<string, string> = {};
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
    const body = new FormData();
    body.append('name', name);
    body.append('categoryId', categoryId);
    body.append('file', file);
    const response = await fetch('/api/visual-templates/import', { method: 'POST', headers, credentials: 'same-origin', body });
    const text = await response.text();
    const payload: unknown = text ? JSON.parse(text) : null;
    if (!response.ok) {
      const detail = (payload as { error?: { code?: string; message?: string } } | null)?.error;
      throw new ApiError(detail?.message ?? 'Le gabarit visuel n’a pas pu être importé.', detail?.code ?? 'UNKNOWN', response.status);
    }
    return payload as { template: { id: string; slug: string; name: string; version: number; categoryId: string; familySlug: string; format: 'square' | 'feed' | 'story' } };
  },
  uploadBrandPortrait: async (file: File, csrfToken: string | undefined): Promise<{ mediaId: string; role: 'portrait' }> => {
    const headers: Record<string, string> = {};
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
    const body = new FormData();
    body.append('file', file);
    const response = await fetch('/api/settings/brand-assets/portrait', { method: 'POST', headers, credentials: 'same-origin', body });
    const text = await response.text();
    const payload: unknown = text ? JSON.parse(text) : null;
    if (!response.ok) {
      const detail = (payload as { error?: { code?: string; message?: string } } | null)?.error;
      throw new ApiError(detail?.message ?? 'Le portrait n’a pas pu être chargé.', detail?.code ?? 'UNKNOWN', response.status);
    }
    return payload as { mediaId: string; role: 'portrait' };
  },

  createTopic: (
    categoryId: string,
    title: string,
    payload: Record<string, string>,
    csrfToken: string | undefined,
    videoTemplateFamilySlug?: string,
  ) =>
    request<{ id: string }>('/api/topics', {
      method: 'POST',
      body: { categoryId, title, ...(Object.keys(payload).length > 0 ? { payload } : {}), ...(videoTemplateFamilySlug ? { videoTemplateFamilySlug } : {}) },
      csrfToken,
    }),

  topics: (status?: string) =>
    request<{ topics: Topic[] }>(`/api/topics${status ? `?status=${encodeURIComponent(status)}` : ''}`),

  // Hors du helper `request` : un envoi multipart laisse le navigateur poser
  // lui-même le Content-Type (avec sa frontière), un `application/json`
  // imposé rendrait le fichier illisible côté serveur.
  uploadTopicMedia: async (
    topicId: string,
    file: File,
    role: 'source_video' | 'fallback_image',
    csrfToken: string | undefined,
  ): Promise<UploadedMedia> => {
    const headers: Record<string, string> = {};
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
    const body = new FormData();
    body.append('file', file);

    const response = await fetch(`/api/topics/${topicId}/media?role=${role}`, {
      method: 'POST',
      headers,
      credentials: 'same-origin',
      body,
    });
    const text = await response.text();
    const payload: unknown = text ? JSON.parse(text) : null;
    if (!response.ok) {
      const detail = (payload as { error?: { code?: string; message?: string } } | null)?.error;
      throw new ApiError(detail?.message ?? 'Le téléversement a échoué.', detail?.code ?? 'UNKNOWN', response.status);
    }
    return payload as UploadedMedia;
  },

  setTopicVideoTemplateFamily: (topicId: string, videoTemplateFamilySlug: string | null, csrfToken: string | undefined) =>
    request<{ videoTemplateFamilySlug: string | null }>(`/api/topics/${topicId}/video-template`, {
      method: 'PUT', body: { videoTemplateFamilySlug }, csrfToken,
    }),

  videoTemplates: () => request<{ templates: VideoTemplate[] }>('/api/video-templates'),
  studioTemplates: () => request<{ templates: VideoTemplate[] }>('/api/studio/templates'),

  createPngTemplate: async (name: string, categoryId: string, format: 'square' | 'feed' | 'story', slideRole: 'cover' | 'detail', file: File, csrfToken: string | undefined): Promise<{ template: { id: string; familySlug: string; format: string; slideRole: 'cover' | 'detail'; width: number; height: number } }> => {
    const headers: Record<string, string> = {};
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
    const body = new FormData();
    body.append('name', name); body.append('categoryId', categoryId); body.append('format', format); body.append('slideRole', slideRole); body.append('file', file);
    const response = await fetch('/api/studio/templates', { method: 'POST', headers, credentials: 'same-origin', body });
    const text = await response.text();
    const payload: unknown = text ? JSON.parse(text) : null;
    if (!response.ok) {
      const detail = (payload as { error?: { code?: string; message?: string } } | null)?.error;
      throw new ApiError(detail?.message ?? 'Le fond Canva n’a pas pu être créé.', detail?.code ?? 'UNKNOWN', response.status);
    }
    return payload as { template: { id: string; familySlug: string; format: string; slideRole: 'cover' | 'detail'; width: number; height: number } };
  },

  importVideoTemplate: async (name: string, file: File, csrfToken: string | undefined): Promise<{ template: Pick<VideoTemplate, 'id' | 'name' | 'format' | 'width' | 'height'> & { familySlug: string } }> => {
    const headers: Record<string, string> = {};
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
    const body = new FormData();
    body.append('name', name);
    body.append('file', file);
    const response = await fetch('/api/video-templates/import', { method: 'POST', headers, credentials: 'same-origin', body });
    const text = await response.text();
    const payload: unknown = text ? JSON.parse(text) : null;
    if (!response.ok) {
      const detail = (payload as { error?: { code?: string; message?: string } } | null)?.error;
      throw new ApiError(detail?.message ?? 'Le gabarit HTML n’a pas pu être importé.', detail?.code ?? 'UNKNOWN', response.status);
    }
    return payload as { template: Pick<VideoTemplate, 'id' | 'name' | 'format' | 'width' | 'height'> & { familySlug: string } };
  },

  uploadVideoTemplateBackground: async (templateId: string, file: File, csrfToken: string | undefined): Promise<{ mediaId: string }> => {
    const headers: Record<string, string> = {};
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
    const body = new FormData();
    body.append('file', file);
    const response = await fetch(`/api/video-templates/${templateId}/background`, { method: 'POST', headers, credentials: 'same-origin', body });
    const text = await response.text();
    const payload: unknown = text ? JSON.parse(text) : null;
    if (!response.ok) {
      const detail = (payload as { error?: { code?: string; message?: string } } | null)?.error;
      throw new ApiError(detail?.message ?? 'Le fond n’a pas pu être chargé.', detail?.code ?? 'UNKNOWN', response.status);
    }
    return payload as { mediaId: string };
  },

  saveVideoTemplateLayout: (templateId: string, body: { backgroundMediaId: string; videoFrame: VideoFrame; fields: VideoTemplateField[]; videoLayer: 'below' | 'above'; mediaKind: 'none' | 'image' | 'video' }, csrfToken: string | undefined) =>
    request<{ layout: { videoFrame: VideoFrame; fields: VideoTemplateField[] } }>(`/api/video-templates/${templateId}/layout`, {
      method: 'PUT', body, csrfToken,
    }),

  generatePost: (topicId: string, csrfToken: string | undefined) =>
    request<GenerationOutcome>(`/api/topics/${topicId}/generate`, { method: 'POST', csrfToken }),

  posts: (params: { from?: string; to?: string; status?: string } = {}) => {
    const query = new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][]);
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return request<{ posts: PostSummary[] }>(`/api/posts${suffix}`);
  },

  reviewQueue: () => request<{ posts: PostSummary[] }>('/api/review-queue'),

  postDetail: (id: string) => request<{ post: PostSummary; variants: PostVariant[] }>(`/api/posts/${id}`),

  updatePostVariants: (postId: string, variants: Array<Pick<PostVariant, 'id' | 'body' | 'hashtags'>>, csrfToken: string | undefined) =>
    request<{ updated: number }>(`/api/posts/${postId}/variants`, {
      method: 'PATCH', body: { variants }, csrfToken,
    }),

  approvePost: (id: string, scheduledFor: string, csrfToken: string | undefined) =>
    request<{ status: string }>(`/api/posts/${id}/approve`, { method: 'POST', body: { scheduledFor }, csrfToken }),

  requestChanges: (id: string, comment: string, csrfToken: string | undefined, postVariantId?: string) =>
    request<{ status: string }>(`/api/posts/${id}/request-changes`, {
      method: 'POST',
      body: postVariantId ? { comment, postVariantId } : { comment },
      csrfToken,
    }),

  rejectPost: (id: string, comment: string, csrfToken: string | undefined) =>
    request<{ status: string }>(`/api/posts/${id}/reject`, { method: 'POST', body: { comment }, csrfToken }),
};
